import {
  AlertTriangle,
  BadgeCheck,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  DoorOpen,
  FileDown,
  GraduationCap,
  Home,
  KanbanSquare,
  KeyRound,
  LayoutGrid,
  LineChart,
  FileBarChart,
  ListChecks,
  LogIn,
  type LucideIcon,
  Map as MapIcon,
  MessageCircle,
  Phone,
  PhoneCall,
  Puzzle,
  Route,
  ScrollText,
  Settings,
  Shield,
  Target,
  TrendingUp,
  Trophy,
  UserMinus,
  UserRound,
  Users,
  UsersRound,
  Wallet,
  BarChart3,
  CreditCard,
  Cable,
  ClipboardCheck,
  Handshake,
  HardHat,
  IndianRupee,
  SlidersHorizontal,
  BriefcaseBusiness,
  HandCoins,
  Megaphone,
  Percent,
  ListPlus,
  ShieldCheck,
} from 'lucide-react';
import type { ComponentType } from 'react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon | ComponentType<{ className?: string }>;
  /** Shown when the member holds any of these (empty = everyone). */
  anyOf: string[];
  section: 'main' | 'admin';
  /**
   * Not listed in the sidebar: the page is reached from another one (Pipeline and Visits are tabs of
   * Leads). Its "g" shortcut, palette entry and phone tab still work.
   */
  hidden?: boolean;
  /**
   * Phone tab bar fill-in (D-140): Today · Leads · Inventory · Visits come first; a role without some of
   * them gets the visible items with the lowest rank in the free places, then "More".
   */
  mobile?: number;
  /** "g <key>" keyboard shortcut. */
  shortcut?: string;
  /** The job this page belongs to: one sidebar entry per area, its pages as tabs (D-123). */
  area?: AreaKey;
  /** Shorter name on the area's tab strip (defaults to `label`). */
  tab?: string;
  /** Settings pages: the group they are listed under on the Settings page. */
  group?: SettingsGroup;
}

export type AreaKey =
  | 'today'
  | 'leads'
  | 'inbox'
  | 'calls'
  | 'marketing'
  | 'insights'
  | 'projects'
  | 'bookings'
  | 'partners'
  | 'team'
  | 'settings';
export type SettingsGroup = 'people' | 'sales' | 'organisation' | 'records';

/**
 * Areas in sidebar order, grouped by the job (D-123): selling, the projects being sold, what happens
 * after a booking, the people around it. An area with one visible page is a plain link; with more, the
 * link opens its first page and the pages show as tabs across the top.
 */
export const AREAS: {
  key: AreaKey;
  label: string;
  icon: LucideIcon;
  zone: 'sell' | 'run' | 'admin';
}[] = [
  { key: 'today', label: 'Today', icon: Home, zone: 'sell' },
  { key: 'leads', label: 'Leads', icon: UserRound, zone: 'sell' },
  { key: 'inbox', label: 'WhatsApp inbox', icon: MessageCircle, zone: 'sell' },
  { key: 'calls', label: 'Calls', icon: Phone, zone: 'sell' },
  { key: 'marketing', label: 'Marketing', icon: TrendingUp, zone: 'sell' },
  { key: 'insights', label: 'Insights', icon: LineChart, zone: 'run' },
  { key: 'projects', label: 'Projects', icon: Building2, zone: 'run' },
  { key: 'bookings', label: 'Bookings', icon: Handshake, zone: 'run' },
  { key: 'partners', label: 'Partners', icon: BriefcaseBusiness, zone: 'run' },
  { key: 'team', label: 'Team', icon: Trophy, zone: 'run' },
  { key: 'settings', label: 'Settings', icon: Settings, zone: 'admin' },
];

export const SETTINGS_GROUPS: { key: SettingsGroup; label: string; detail: string }[] = [
  { key: 'people', label: 'People and access', detail: 'Who works here, their teams and what they can see.' },
  {
    key: 'sales',
    label: 'Leads and bookings',
    detail: 'Where leads come from, who gets them, booking rules.',
  },
  {
    key: 'organisation',
    label: 'Organisation',
    detail: 'Company details, features, sign-in and connected services.',
  },
  { key: 'records', label: 'Records', detail: 'What happened, what was exported, what failed.' },
];

/** The nav item a path belongs to: the longest `to` that is the path or a parent of it. */
export function matchNav(items: NavItem[], path: string): NavItem | undefined {
  let best: NavItem | undefined;
  for (const i of items) {
    const hit = i.to === '/' ? path === '/' : path === i.to || path.startsWith(i.to + '/');
    if (hit && (!best || i.to.length > best.to.length)) best = i;
  }
  return best;
}

/**
 * Navigation registry. Features add their item when their screens exist — the menu never shows
 * a page that isn't built. Extension modules append through the `sidebar` UI slot.
 */
export const NAV: NavItem[] = [
  {
    to: '/',
    label: 'Today',
    icon: Home,
    anyOf: [],
    section: 'main',
    area: 'today',
    mobile: 1,
    shortcut: 't',
  },
  {
    to: '/leads',
    label: 'Leads',
    icon: UserRound,
    anyOf: ['leads.lead.read'],
    section: 'main',
    area: 'leads',
    mobile: 2,
    shortcut: 'l',
  },
  // Walk-in quick capture (UX §4.7): in the phone tab bar, because it happens standing at the site
  // office; on a larger screen it is a button on the Leads page.
  {
    to: '/walk-in',
    label: 'Walk-in',
    icon: DoorOpen,
    anyOf: ['leads.lead.create'],
    section: 'main',
    area: 'leads',
    hidden: true,
    mobile: 3,
    shortcut: 'w',
  },
  {
    to: '/visits',
    label: 'Visits',
    icon: CalendarDays,
    anyOf: ['activity.visit.read'],
    section: 'main',
    area: 'leads',
    hidden: true,
    shortcut: 'v',
  },
  {
    to: '/pipeline',
    label: 'Pipeline',
    icon: KanbanSquare,
    anyOf: ['leads.lead.read'],
    section: 'main',
    area: 'leads',
    hidden: true,
    shortcut: 'k',
  },
  // Comms (BUILD_BRIEF §9.4): the shared WhatsApp inbox, the call log with coaching, the training
  // library and call/message reports.
  {
    to: '/inbox',
    label: 'WhatsApp inbox',
    icon: MessageCircle,
    anyOf: ['comms.message.read'],
    section: 'main',
    area: 'inbox',
    mobile: 4,
    shortcut: 'b',
  },
  {
    to: '/calls',
    label: 'Calls',
    icon: Phone,
    anyOf: ['comms.call.read'],
    section: 'main',
    area: 'calls',
    tab: 'Calls',
    shortcut: 'c',
  },
  {
    to: '/training-library',
    label: 'Training library',
    icon: GraduationCap,
    anyOf: ['comms.recording.play'],
    section: 'main',
    area: 'calls',
    tab: 'Training library',
  },
  {
    to: '/reports/comms',
    label: 'Call reports',
    icon: BarChart3,
    anyOf: ['comms.call.read', 'comms.message.read'],
    section: 'main',
    area: 'calls',
    tab: 'Reports',
  },
  // Insights (M12, D-137, D-138): trends against the previous period, and the custom report builder.
  {
    to: '/insights',
    label: 'Trends',
    icon: LineChart,
    anyOf: ['analytics.dashboard.read'],
    section: 'main',
    area: 'insights',
    tab: 'Trends',
  },
  {
    to: '/insights/reports',
    label: 'Reports',
    icon: FileBarChart,
    anyOf: ['analytics.report.build'],
    section: 'main',
    area: 'insights',
    tab: 'Reports',
  },
  // Marketing (M9, D-135): closed-loop attribution, the source scorecard and the ad accounts.
  {
    to: '/marketing',
    label: 'Attribution',
    icon: TrendingUp,
    anyOf: ['marketing.campaign.read'],
    section: 'main',
    area: 'marketing',
    tab: 'Attribution',
  },
  {
    to: '/marketing/sources',
    label: 'Source scorecard',
    icon: BarChart3,
    anyOf: ['marketing.campaign.read'],
    section: 'main',
    area: 'marketing',
    tab: 'Sources',
  },
  {
    to: '/marketing/campaigns',
    label: 'Campaigns',
    icon: Megaphone,
    anyOf: ['marketing.campaign.read'],
    section: 'main',
    area: 'marketing',
    tab: 'Campaigns',
  },
  {
    to: '/marketing/ad-accounts',
    label: 'Ad accounts',
    icon: Wallet,
    anyOf: ['marketing.spend.manage', 'marketing.campaign.read'],
    section: 'main',
    area: 'marketing',
    tab: 'Ad accounts',
  },
  {
    to: '/marketing/settings',
    label: 'Marketing settings',
    icon: SlidersHorizontal,
    anyOf: ['marketing.campaign.manage'],
    section: 'main',
    area: 'marketing',
    tab: 'Settings',
  },
  {
    to: '/inventory',
    label: 'Inventory',
    icon: LayoutGrid,
    anyOf: ['inventory.unit.read'],
    section: 'main',
    area: 'projects',
    tab: 'Inventory',
    mobile: 6,
    shortcut: 'i',
  },
  {
    to: '/projects',
    label: 'Projects',
    icon: Building2,
    anyOf: ['inventory.project.read'],
    section: 'main',
    area: 'projects',
    tab: 'Projects',
    shortcut: 'p',
  },
  {
    to: '/site-plans',
    label: 'Site plans',
    icon: MapIcon,
    anyOf: ['siteplan.plan.read'],
    section: 'main',
    area: 'projects',
    tab: 'Site plans',
  },
  {
    to: '/approvals',
    label: 'Approvals',
    icon: BadgeCheck,
    anyOf: ['inventory.discount.approve'],
    section: 'main',
    area: 'projects',
    tab: 'Price approvals',
  },
  // Post-sales (M13)
  {
    to: '/bookings',
    label: 'Bookings',
    icon: Handshake,
    anyOf: ['postsales.booking.read'],
    section: 'main',
    area: 'bookings',
    tab: 'Bookings',
  },
  {
    to: '/receivables',
    label: 'Receivables',
    icon: IndianRupee,
    anyOf: ['postsales.receipt.read'],
    section: 'main',
    area: 'bookings',
    tab: 'Receivables',
  },
  {
    to: '/booking-approvals',
    label: 'Booking approvals',
    icon: ClipboardCheck,
    anyOf: ['postsales.cancellation.approve', 'postsales.refund.approve'],
    section: 'main',
    area: 'bookings',
    tab: 'Approvals',
  },
  {
    to: '/construction-updates',
    label: 'Construction updates',
    icon: HardHat,
    anyOf: ['postsales.construction.manage'],
    section: 'main',
    area: 'bookings',
    tab: 'Construction updates',
  },
  // Channel partners (M14): firms and their approval, commission plans, the payout queue, broadcasts.
  {
    to: '/partners',
    label: 'Channel partners',
    icon: BriefcaseBusiness,
    anyOf: ['partners.partner.read'],
    section: 'main',
    area: 'partners',
    tab: 'Partners',
  },
  {
    to: '/partners/commission-plans',
    label: 'Commission plans',
    icon: Percent,
    anyOf: ['partners.commission.read'],
    section: 'main',
    area: 'partners',
    tab: 'Commission plans',
  },
  {
    to: '/partners/payouts',
    label: 'Partner payouts',
    icon: HandCoins,
    anyOf: ['partners.commission.read'],
    section: 'main',
    area: 'partners',
    tab: 'Payouts',
  },
  {
    to: '/partners/broadcasts',
    label: 'Partner broadcasts',
    icon: Megaphone,
    anyOf: ['partners.partner.read'],
    section: 'main',
    area: 'partners',
    tab: 'Broadcasts',
  },
  // Team operations (M10)
  {
    to: '/targets',
    label: 'Targets',
    icon: Target,
    anyOf: ['team.target.read'],
    section: 'main',
    area: 'team',
    tab: 'Targets',
  },
  {
    to: '/leaderboard',
    label: 'Leaderboard',
    icon: Trophy,
    anyOf: ['team.leaderboard.read'],
    section: 'main',
    area: 'team',
    tab: 'Leaderboard',
  },
  {
    to: '/incentives',
    label: 'Incentives',
    icon: Wallet,
    anyOf: ['team.incentive.read'],
    section: 'main',
    area: 'team',
    tab: 'Incentives',
  },
  {
    to: '/attendance',
    label: 'Attendance & shifts',
    icon: CalendarClock,
    anyOf: ['team.attendance.read', 'team.attendance.self'],
    section: 'main',
    area: 'team',
    tab: 'Attendance',
  },
  {
    to: '/admin/members',
    label: 'Members',
    icon: Users,
    anyOf: ['org.member.read'],
    section: 'admin',
    area: 'settings',
    group: 'people',
    shortcut: 'm',
  },
  {
    to: '/admin/leads',
    label: 'Capture & routing',
    icon: Route,
    anyOf: ['leads.source.manage', 'leads.routing.manage'],
    section: 'admin',
    area: 'settings',
    group: 'sales',
  },
  {
    to: '/admin/payment-gateway',
    label: 'Payment gateway',
    icon: CreditCard,
    anyOf: ['postsales.gateway.manage'],
    section: 'admin',
    area: 'settings',
    group: 'sales',
  },
  {
    to: '/admin/post-sales',
    label: 'Post-sales',
    icon: SlidersHorizontal,
    anyOf: ['postsales.settings.manage'],
    section: 'admin',
    area: 'settings',
    group: 'sales',
  },
  {
    to: '/admin/handover',
    label: 'Exit handover',
    icon: UserMinus,
    anyOf: ['org.member.offboard'],
    section: 'admin',
    area: 'settings',
    group: 'people',
  },
  {
    to: '/admin/teams',
    label: 'Teams',
    icon: UsersRound,
    anyOf: ['org.team.read'],
    section: 'admin',
    area: 'settings',
    group: 'people',
  },
  {
    to: '/admin/roles',
    label: 'Roles & access',
    icon: Shield,
    anyOf: ['org.role.read'],
    section: 'admin',
    area: 'settings',
    group: 'people',
  },
  {
    to: '/admin/audit',
    label: 'Audit log',
    icon: ScrollText,
    anyOf: ['audit.log.read'],
    section: 'admin',
    area: 'settings',
    group: 'records',
  },
  {
    to: '/admin/exports',
    label: 'Export log',
    icon: FileDown,
    anyOf: ['audit.exportlog.read'],
    section: 'admin',
    area: 'settings',
    group: 'records',
  },
  {
    to: '/setup',
    label: 'Set-up wizard',
    icon: ListChecks,
    anyOf: ['org.onboarding.manage'],
    section: 'admin',
    area: 'settings',
    group: 'organisation',
  },
  {
    to: '/admin/sso',
    label: 'Single sign-on',
    icon: LogIn,
    anyOf: ['org.sso.manage'],
    section: 'admin',
    area: 'settings',
    group: 'organisation',
  },
  {
    to: '/admin/events',
    label: 'Failed events',
    icon: AlertTriangle,
    anyOf: ['admin.event.manage'],
    section: 'admin',
    area: 'settings',
    group: 'records',
  },
  {
    to: '/admin/integrations',
    label: 'Integrations',
    icon: Cable,
    anyOf: ['comms.config.manage', 'admin.integration.manage'],
    section: 'admin',
    area: 'settings',
    group: 'organisation',
  },
  {
    to: '/admin/modules',
    label: 'Modules',
    icon: Puzzle,
    anyOf: ['admin.module.manage'],
    section: 'admin',
    area: 'settings',
    group: 'organisation',
  },
  {
    to: '/admin/privacy',
    label: 'Data protection',
    icon: ShieldCheck,
    anyOf: ['admin.dpdp.manage'],
    section: 'admin',
    area: 'settings',
    group: 'records',
  },
  {
    to: '/admin/customise',
    label: 'Fields, lists & rules',
    icon: ListPlus,
    anyOf: ['org.settings.update'],
    section: 'admin',
    area: 'settings',
    group: 'organisation',
  },
  {
    to: '/admin/settings',
    label: 'Settings',
    icon: Settings,
    anyOf: ['org.settings.read'],
    section: 'admin',
    area: 'settings',
    group: 'organisation',
  },
];

export const PROFILE_ITEM = { to: '/profile', label: 'My profile', icon: KeyRound };
export const QUICK_ICONS = { task: ClipboardList, visit: CalendarCheck, call: PhoneCall };

/** Routes that exist in this build; items for routes not yet registered are hidden. */
export const registeredRoutes = new Set<string>();

export function visibleNav(can: (p: string) => boolean, extra: NavItem[] = []): NavItem[] {
  const allowed = (n: NavItem) => n.anyOf.length === 0 || n.anyOf.some(can);
  return [...NAV.filter((n) => registeredRoutes.has(n.to) && allowed(n)), ...extra.filter(allowed)];
}

export const MODULE_ICON = Puzzle;
